/**
 * FIT File Service
 * Handles FIT file parsing using Web Worker for better memory management
 */

import { markRaw } from 'vue'

import { compressFileWithGzip } from '@/services/fileCompressionService'
import { generateId } from '@/services/idService'
import { showError } from '@/services/toastService'
import type { Tour, Track, Waypoint } from '@/types/tour'
import { ensureImportedRoutePoints } from '@/utils/importRoutePointFallback'
import { truncateToMaxLength } from '@/utils/validation'

// Message types for Web Worker communication
interface WorkerProgressMessage {
  type: 'progress'
  progress: number
  message?: string
}

interface WorkerResultMessage {
  type: 'result'
  tour: {
    name: string
    tracks: Array<{
      name: string
      segments: Array<{
        points: Array<{
          lat: number
          lon: number
          ele?: number
          time?: number
          [key: string]: number | undefined
        }>
      }>
      metrics: {
        distance: number
        timeMoving: number | null
        timeElapsed: number | null
        elevationGain: number | null
        elevationLoss: number | null
        elevationMin: number | null
        elevationMax: number | null
      }
      startTime?: number
      endTime?: number
      type?: string
    }>
    waypoints: Array<never>
    distance: number
    time_moving: number | null
    time_elapsed: number | null
  }
}

interface WorkerErrorMessage {
  type: 'error'
  error: string
}

type WorkerMessage = WorkerProgressMessage | WorkerResultMessage | WorkerErrorMessage

/**
 * Create and manage a Web Worker for FIT file processing
 */
class FitWorkerManager {
  private worker: Worker | null = null
  private processingCallback: ((progress: number, message?: string) => void) | null = null

  /**
   * Initialize the worker if not already created
   */
  private initWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL('../workers/fitWorker.ts', import.meta.url), {
        type: 'module',
      })
    }
    return this.worker
  }

  /**
   * Process a FIT file using the Web Worker
   */
  async processFile(
    arrayBuffer: ArrayBuffer,
    fileName: string,
    onProgress?: (progress: number, message?: string) => void,
  ): Promise<Omit<Tour, 'clientId'>> {
    return new Promise((resolve, reject) => {
      const worker = this.initWorker()
      this.processingCallback = onProgress || null

      // Set up message handler
      const messageHandler = (event: MessageEvent<WorkerMessage>) => {
        const message = event.data

        switch (message.type) {
          case 'progress':
            if (this.processingCallback) {
              this.processingCallback(message.progress, message.message)
            }
            break

          case 'result':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)

            // Convert worker result to Tour format
            const tour = this.convertWorkerResultToTour(message.tour, fileName)
            resolve(tour)
            break

          case 'error':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error(message.error))
            break
        }
      }

      // Set up error handler
      const errorHandler = (error: ErrorEvent) => {
        worker.removeEventListener('message', messageHandler)
        worker.removeEventListener('error', errorHandler)
        reject(new Error(`Worker error: ${error.message}`))
      }

      worker.addEventListener('message', messageHandler)
      worker.addEventListener('error', errorHandler)

      // Send data to worker
      worker.postMessage(
        {
          type: 'parse',
          data: arrayBuffer,
          fileName: fileName,
        },
        [arrayBuffer as ArrayBuffer],
      )
    })
  }

  /**
   * Convert worker result to Tour format with proper IDs
   */
  private convertWorkerResultToTour(
    workerTour: WorkerResultMessage['tour'],
    fileName: string,
  ): Omit<Tour, 'clientId'> {
    // Process tracks
    const tracks: Track[] = workerTour.tracks.map((workerTrack) => {
      const track: Track = {
        clientId: generateId(),
        name: truncateToMaxLength(workerTrack.name, 255, 'Track name') || workerTrack.name,
        segments: workerTrack.segments.map((segment) => {
          const pts = segment.points as Array<{
            lat: number
            lon: number
            ele?: number
            time?: number
          }>
          for (let i = 0; i < pts.length; i++) {
            ;(pts[i] as unknown as Waypoint).clientId = generateId()
          }
          return {
            points: markRaw(pts) as unknown as Waypoint[],
          }
        }),
        visible: true,
        metrics: {
          ...workerTrack.metrics,
          elevationMin: null, // Will be calculated if needed
          elevationMax: null, // Will be calculated if needed
        },
        startTime: workerTrack.startTime,
        endTime: workerTrack.endTime,
        type: workerTrack.type,
      }

      return track
    })

    // Create tour name
    const name =
      truncateToMaxLength(fileName.replace(/\.fit$/i, ''), 255, 'Activity name') ||
      fileName.replace(/\.fit$/i, '')
    const routePoints = ensureImportedRoutePoints([], tracks, generateId)

    return {
      name,
      tracks,
      waypoints: [], // FIT files typically don't have waypoints
      routePoints,
      distance: workerTour.distance,
      time_moving: workerTour.time_moving,
      time_elapsed: workerTour.time_elapsed,
    }
  }

  /**
   * Terminate the worker to free resources
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
    this.processingCallback = null
  }
}

// Singleton instance
let workerManager: FitWorkerManager | null = null

/**
 * Parse FIT file content into tour data using Web Worker
 * @param fitContent The FIT file content as ArrayBuffer
 * @param fileName Original filename for fallback naming
 * @param originalFile The original FIT file for storage
 * @param onProgress Optional progress callback
 * @returns Parsed tour data (without ID)
 */
export async function parseFIT(
  fitContent: ArrayBuffer,
  fileName: string,
  originalFile?: File,
  onProgress?: (progress: number, message?: string) => void,
): Promise<Omit<Tour, 'clientId'>> {
  try {
    // Check file size limit (100MB for FIT files)
    const MAX_FIT_FILE_SIZE = 100 * 1024 * 1024
    if (fitContent.byteLength > MAX_FIT_FILE_SIZE) {
      throw new Error(`FIT file too large. Maximum size is ${MAX_FIT_FILE_SIZE / (1024 * 1024)}MB`)
    }

    // Initialize worker manager if needed
    if (!workerManager) {
      workerManager = new FitWorkerManager()
    }

    // Process the file using Web Worker
    const tour = await workerManager.processFile(fitContent, fileName, onProgress)

    // Track the source filename for downstream sync payloads
    tour.sourceFilename = truncateToMaxLength(originalFile?.name || fileName, 4096)

    // Add the original file reference if provided
    if (originalFile) {
      const { file: compressedFile, compressed } = await compressFileWithGzip(originalFile)
      tour._originalFile = compressedFile
      tour.eventFileOriginalName = originalFile.name
      tour.eventFileName = compressedFile.name
      tour.eventFileMimeType = compressedFile.type
      tour.eventFileCompressed = compressed
    }

    return tour
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    showError(`Error parsing FIT file: ${message}`)
    throw new Error(`Error parsing FIT file: ${message}`)
  }
}
